import React, { useContext, useEffect, useState, forwardRef, useRef } from 'react';
import classNames from 'classnames';
import omit from 'omit.js';
import { composeRef } from 'rc-util/es/ref';
import {ConfigContext} from "../../wui-provider/src/context";
import { LayoutContextProps, BasicPropsWithTagName, GeneratorProps } from './iLayout';
import { fillSpace as fillSpaceFunc, getNid} from "../../wui-core/src/index"
export const LayoutContext = React.createContext<LayoutContextProps>({
    siderHook: {
        addSider: () => null,
        removeSider: () => null,
    },
});

export interface AdapterPropTypes extends BasicPropsWithTagName {
    forwardedRef?: React.Ref<HTMLElement | undefined>;
}

const generator = ({suffixCls, tagName, displayName}: GeneratorProps) => {
    return (BasicComponent: any) => {
        const Adapter: React.FC<AdapterPropTypes> = (props) => {
            const {forwardedRef} = props;
            const {getPrefixCls} = useContext(ConfigContext);
            const {prefixCls: customizePrefixCls} = props;
            const prefixCls = getPrefixCls(suffixCls, customizePrefixCls);

            return <BasicComponent prefixCls={prefixCls} tagName={tagName} {...omit(props, ["forwardedRef", "tagName"])}
								   ref={forwardedRef}/>;
        };
        Adapter.displayName = displayName;

        return forwardRef<HTMLElement, AdapterPropTypes>((props, ref) => {
            const innerRef = useRef<HTMLElement>();
            const mergedRef = composeRef(ref, innerRef);
            return <Adapter {...props} forwardedRef={mergedRef}/>;
        });
    };
}

// 组件Header, Footer, Content实例
const Basic = forwardRef<HTMLElement, BasicPropsWithTagName>((props, ref) => {
    const {prefixCls, className, children, tagName = 'div', ...others} = props;
    const classString = classNames(prefixCls, className);
    return React.createElement(tagName, {className: classString, ...others, ref: ref}, children);
});
Basic.displayName = 'Basic';

// 获取父容器元素，填充layout
const setLayoutHeightByfillSpace = (ref : HTMLElement | null) => {
    ref && fillSpaceFunc(ref, undefined, (_element, _parent, _noPaddingMaxW, _noPaddingMaxH) => {
        ref.style.height = _noPaddingMaxH + "px"
    })
}
// 组件layout实例
const BasicLayout = forwardRef<HTMLElement, BasicPropsWithTagName>((props, ref) => {
    const [siders, setSiders] = useState<string[]>([]);

    const {prefixCls, className, children, hasSider, tagName: ComponentTag = 'section', fillSpace, ...others} = props;
    const classString = classNames(
        prefixCls,
        {
            [`${prefixCls}-has-sider`]: typeof hasSider === 'boolean' ? hasSider : siders.length > 0,
        },
        className,
    );

    useEffect(()=>{
        let domRef = null;
        if (ref && "current" in ref && ref.current) {
            domRef = ref?.current;
        }
        fillSpace && setLayoutHeightByfillSpace(domRef)
    }, [ref, fillSpace])
    let adapterNid = getNid(props)

    return (
        <LayoutContext.Provider
            value={{
                siderHook: {
                    addSider: (id: string) => {
                        setSiders(prev => [...prev, id]);
                    },
                    removeSider: (id: string) => {
                        setSiders(prev => prev.filter(currentId => currentId !== id));
                    },
                },
            }}
        >
            <ComponentTag className={classString} {...others} ref={ref} {...adapterNid}>
                {children}
            </ComponentTag>
        </LayoutContext.Provider>
    );
});
BasicLayout.displayName = 'BasicLayout';


const Layout = generator({
    suffixCls: 'layout',
    tagName: 'section',
    displayName: 'LayoutContainer',
})(BasicLayout);

const Header = generator({
    suffixCls: 'layout-header',
    tagName: 'header',
    displayName: 'Header',
})(Basic);

const Footer = generator({
    suffixCls: 'layout-footer',
    tagName: 'footer',
    displayName: 'Footer',
})(Basic);

const Content = generator({
    suffixCls: 'layout-content',
    tagName: 'main',
    displayName: 'Content',
})(Basic);

export {Header, Footer, Content};
export default Layout;
